/**
 * SILISENSE roles and permissions.
 * platform_admin: Silisense staff — full system (Admin page, all tenants).
 * admin: tenant-level administrator for one institution.
 * reviewer: edit rights within tenant; no platform Admin page.
 * reader: read-only within tenant.
 * report_reader: dashboard + reports + settings only.
 */

export const ROLES = {
  PLATFORM_ADMIN: 'platform_admin',
  ADMIN: 'admin',
  REVIEWER: 'reviewer',
  READER: 'reader',
  REPORT_READER: 'report_reader',
} as const;

/** Roles assignable to users for a tenant (platform admin UI). */
export const TENANT_ASSIGNABLE_ROLES = [
  ROLES.ADMIN,
  ROLES.REVIEWER,
  ROLES.READER,
  ROLES.REPORT_READER,
] as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ROLE_LABELS: Record<Role, string> = {
  [ROLES.PLATFORM_ADMIN]: 'Platform admin',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.REVIEWER]: 'Reviewer',
  [ROLES.READER]: 'Reader',
  [ROLES.REPORT_READER]: 'Report reader',
};

export function isPlatformAdmin(role: Role | undefined): boolean {
  return role === ROLES.PLATFORM_ADMIN;
}

export function isTenantAdmin(role: Role | undefined): boolean {
  return role === ROLES.ADMIN;
}

/** Can edit submissions, reports, settings, consortium (within tenant scope). */
export function canEdit(role: Role | undefined): boolean {
  return isPlatformAdmin(role) || isTenantAdmin(role) || role === ROLES.REVIEWER;
}

/** Admin page — platform controls for platform_admin, tenant integration keys for tenant admin. */
export function canAccessAdmin(role: Role | undefined): boolean {
  return isPlatformAdmin(role) || isTenantAdmin(role);
}

/** Analytics, PeerMark (instructor), Rubrics — same gate as Admin (not under Admin tabs). */
export function canAccessAnalytics(role: Role | undefined): boolean {
  return canAccessAdmin(role);
}
export function canAccessPeerMarkAdmin(role: Role | undefined): boolean {
  return canAccessAdmin(role);
}
export function canAccessRubrics(role: Role | undefined): boolean {
  return canAccessAdmin(role);
}

export function canAccessReports(role: Role | undefined): boolean {
  return !!role;
}

export function canAccessSubmissions(role: Role | undefined): boolean {
  return (
    isPlatformAdmin(role) ||
    isTenantAdmin(role) ||
    role === ROLES.REVIEWER ||
    role === ROLES.READER
  );
}

export function canAccessConsortium(role: Role | undefined): boolean {
  return (
    isPlatformAdmin(role) ||
    isTenantAdmin(role) ||
    role === ROLES.REVIEWER ||
    role === ROLES.READER
  );
}

export function canAccessSettings(role: Role | undefined): boolean {
  return !!role;
}

export function canAccessDashboard(role: Role | undefined): boolean {
  return !!role;
}

export function canAccessIntegrations(role: Role | undefined): boolean {
  return isPlatformAdmin(role) || isTenantAdmin(role) || role === ROLES.REVIEWER;
}

export function canAccessTraining(role: Role | undefined): boolean {
  return (
    isPlatformAdmin(role) ||
    isTenantAdmin(role) ||
    role === ROLES.REVIEWER ||
    role === ROLES.READER
  );
}

/** PeerMark inbox — any provisioned user; API enforces allocation ownership. */
export function canAccessPeerReviews(role: Role | undefined): boolean {
  return !!role;
}

/** In-app AI Support (Sam) — any authenticated role. */
export function canAccessSupport(role: Role | undefined): boolean {
  return !!role;
}

export function getNavItems(role: Role | undefined): { to: string; label: string }[] {
  const all = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/submissions', label: 'Submissions' },
    { to: '/reports', label: 'Reports' },
    { to: '/analytics', label: 'Analytics' },
    { to: '/peermark', label: 'PeerMark' },
    { to: '/rubrics', label: 'Rubrics' },
    { to: '/peer-reviews', label: 'Peer reviews' },
    { to: '/training', label: 'Training' },
    { to: '/consortium', label: 'Consortium' },
    { to: '/integrations', label: 'Integrations' },
    { to: '/settings', label: 'Settings' },
    { to: '/admin', label: 'Admin' },
  ];
  if (!role) return [];
  if (role === ROLES.REPORT_READER) {
    return [
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/reports', label: 'Reports' },
      { to: '/peer-reviews', label: 'Peer reviews' },
      { to: '/settings', label: 'Settings' },
    ];
  }
  const items = all.filter((item) => {
    if (item.to === '/admin') return canAccessAdmin(role);
    if (item.to === '/analytics') return canAccessAnalytics(role);
    if (item.to === '/peermark') return canAccessPeerMarkAdmin(role);
    if (item.to === '/rubrics') return canAccessRubrics(role);
    if (item.to === '/submissions') return canAccessSubmissions(role);
    if (item.to === '/consortium') return canAccessConsortium(role);
    if (item.to === '/integrations') return canAccessIntegrations(role);
    if (item.to === '/training') return canAccessTraining(role);
    if (item.to === '/peer-reviews') return canAccessPeerReviews(role);
    return true;
  });
  return items;
}

export function parseRole(value: unknown): Role {
  if (
    value === ROLES.PLATFORM_ADMIN ||
    value === ROLES.ADMIN ||
    value === ROLES.REVIEWER ||
    value === ROLES.READER ||
    value === ROLES.REPORT_READER
  ) {
    return value;
  }
  return ROLES.READER;
}
